import type { ReadAPIDefinitionType } from '@readme/api-contracts/schemas/apis';
import type {
  ReadCustomBlockGitCollectionType,
  ReadCustomBlockGitUnpaginatedCollectionType,
} from '@readme/api-contracts/schemas/customblock';
import type { CustomPagePrefetchReadRepresentationType } from '@readme/api-contracts/schemas/custompage';
import type { GuidePrefetchReadRepresentationType, ReadGuideType } from '@readme/api/mappings/page/guide/types';
import type { ReferencePrefetchReadRepresentationType } from '@readme/api/mappings/page/reference/types';
import type { SuperHubRouteParams } from '@routes/SuperHub/types';
import type { ReactNode } from 'react';

import { produce } from 'immer';
import lodashMerge from 'lodash/merge';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import useIsomorphicLayoutEffect from '@core/hooks/useIsomorphicLayoutEffect';
import useReadmeApi, { useReadmeApiNext } from '@core/hooks/useReadmeApi';
import { useProjectStore } from '@core/store/Project';

import { superHubStore, useSuperHubStore } from '..';
import { extractApiDefinitionFilename } from '../util';

import { getDefaultDocument } from './defaults';
import { isReferencePage } from './util';

/**
 * Union type for all prefetch response types. Each section has a different
 * response shape based on what data it needs.
 */
type PrefetchDataType =
  | CustomPagePrefetchReadRepresentationType
  | GuidePrefetchReadRepresentationType
  | ReferencePrefetchReadRepresentationType;

/**
 * Sections that support prefetch consolidation.
 */
const PREFETCH_SECTIONS = ['reference', 'page'] as const;
type PrefetchSection = (typeof PREFETCH_SECTIONS)[number];

/**
 * Map route sections to their API prefetch paths.
 */
const PREFETCH_PATH_MAP: Record<PrefetchSection, string> = {
  reference: 'reference',
  page: 'custom_pages',
};

interface ConnectSuperHubDocumentToApiProps {
  children: ReactNode;
  /**
   * How often SWR should revalidate page data. Default is disabled.
   */
  refreshInterval?: number;
  /**
   * Whether SWR should revalidate page data on focus. Default is `false`.
   */
  revalidateOnFocus?: boolean;
  /** Enables SWR Suspense and sync store initialization for editor routes. */
  suspense?: boolean;
}

/**
 * Connects our SuperHub store's document slice to the page API endpoint via SWR
 * based on the current route section and slug. Fetches new data whenever
 * rendered and updates the store.
 */
export function ConnectSuperHubDocumentToApi({
  children,
  refreshInterval,
  revalidateOnFocus = false,
  suspense = false,
}: ConnectSuperHubDocumentToApiProps) {
  const { section, slug } = useParams<SuperHubRouteParams>();

  const isExperimentalPerformanceEnabled = useProjectStore(
    s => s.data?.reference?.experimental_performance_mode === 'enabled',
  );
  const [
    getApiUrl,
    getApiBaseUrl,
    initialize,
    isEditing,
    isCreateNewPage,
    apiDefinitions,
    defaultApiOptions,
    isI18nEnabled,
    isPrefetchEnabled,
    language,
    prefetchFailed,
    setPrefetchFailed,
  ] = useSuperHubStore(s => [
    s.document.getApiUrl,
    s.getApiBaseUrl,
    s.document.initialize,
    s.isEditing,
    s.editor.isCreateNewPage,
    s.apiDefinitions.data,
    s.defaultApiOptions,
    s.isI18nEnabled,
    s.isPrefetchEnabled,
    s.language,
    s.document.prefetchFailed,
    s.document.setPrefetchFailed,
  ]);

  useEffect(() => {
    setPrefetchFailed(false);
  }, [setPrefetchFailed, section, slug]);

  const firstApiDefinition = apiDefinitions?.[0];
  const apiUrl = slug ? getApiUrl({ slug }) : null;

  const queryString = new URLSearchParams({
    // if we're in view mode and the section is reference, we need
    // the dereferenced/full API definition for the document
    ...(!isEditing &&
      section === 'reference' && {
        reduce: isExperimentalPerformanceEnabled ? 'true' : 'false',
      }),

    // when fetching a changelog document, we need to include the language
    // parameter if i18n is enabled for the project. changelog translations
    // are stored in mongo instead of in a git branch like other docs.
    ...(isI18nEnabled && section === 'changelog' && { lang: language }),
  }).toString();

  const prefetchSection = PREFETCH_SECTIONS.includes(section as PrefetchSection) ? (section as PrefetchSection) : null;
  const prefetchEligible = isPrefetchEnabled && prefetchSection !== null && !isEditing && !isCreateNewPage;
  const shouldAttemptPrefetch = prefetchEligible && !prefetchFailed;

  const prefetchQueryString = useMemo(() => {
    if (!shouldAttemptPrefetch || !prefetchSection) return '';
    const params = new URLSearchParams();
    if (slug) params.set('slug', slug);
    // Reference pages need dereferencing options — align reduce with experimental performance mode
    if (prefetchSection === 'reference') {
      params.set('dereference', 'true');
      params.set('reduce', isExperimentalPerformanceEnabled ? 'true' : 'false');
    }
    return params.toString();
  }, [isExperimentalPerformanceEnabled, prefetchSection, shouldAttemptPrefetch, slug]);

  const prefetchUrl = useMemo(() => {
    if (!shouldAttemptPrefetch || !prefetchSection) return null;
    const basePath = `${getApiBaseUrl()}/${PREFETCH_PATH_MAP[prefetchSection]}:prefetch`;
    return prefetchQueryString ? `${basePath}?${prefetchQueryString}` : basePath;
  }, [getApiBaseUrl, prefetchQueryString, prefetchSection, shouldAttemptPrefetch]);

  const {
    data: prefetchData,
    error: prefetchError,
    isLoading: isPrefetchLoading,
    swrKey: swrKeyPrefetch,
  } = useReadmeApi<PrefetchDataType>(prefetchUrl, {
    ...defaultApiOptions,
    swr: {
      refreshInterval,
      revalidateOnFocus,
      shouldRetryOnError: true,
      ...(suspense ? { suspense: true } : {}),
    },
  });

  const prefetchMissingFields = useMemo(() => {
    if (!shouldAttemptPrefetch || !prefetchSection) return [];
    const missing: string[] = [];
    if (!prefetchData) {
      return isPrefetchLoading ? [] : ['response'];
    }
    // Note: file being null is a valid "page not found" response (e.g. navigating to a
    // non-existent slug). Both the prefetch and legacy paths produce document.data = null
    // in that case, so there is no benefit to falling back — we accept null file as valid.
    // Only check for custom_blocks on sections that support them (reference and docs/guides)
    if (
      slug &&
      prefetchSection !== 'page' &&
      !('custom_blocks' in prefetchData && Array.isArray(prefetchData.custom_blocks?.data))
    ) {
      missing.push('custom_blocks');
    }
    return missing;
  }, [isPrefetchLoading, prefetchData, prefetchSection, shouldAttemptPrefetch, slug]);

  const shouldFallbackToLegacy = shouldAttemptPrefetch && (prefetchError || prefetchMissingFields.length > 0);

  // Trigger fallback to legacy when prefetch data is incomplete or errored.
  useEffect(() => {
    if (shouldFallbackToLegacy && !prefetchFailed) {
      setPrefetchFailed(true);
    }
  }, [prefetchFailed, setPrefetchFailed, shouldFallbackToLegacy]);

  const documentUrl = shouldAttemptPrefetch ? null : apiUrl && queryString ? `${apiUrl}?${queryString}` : apiUrl;
  // Memoized so the changelog default's `new Date()` doesn't churn `documentData`
  // every render and trigger an infinite `document.initialize` loop.
  const createNewPageFallback = useMemo(
    () => (isCreateNewPage ? { data: getDefaultDocument(section, superHubStore.getState().user) } : undefined),
    [isCreateNewPage, section],
  );
  const {
    data: documentData = createNewPageFallback,
    etag: documentEtag,
    isLoading: isDocumentLoading,
    swrKey: swrKeyDocument,
  } = useReadmeApi<ReadGuideType>(documentUrl, {
    ...defaultApiOptions,
    swr: {
      refreshInterval,
      revalidateOnFocus,
      shouldRetryOnError: true,
      ...(suspense ? { suspense: true } : {}),
    },
  });

  // Temporarily limit custom block data fetching to only the docs and reference sections.
  // We'll want to load custom blocks for custom pages and changelogs once there's API support.
  const isCustomBlockRoute = ['docs', 'reference'].includes(section || '');

  // If the document is MDX renderable, we can fetch MDX custom blocks
  const customBlocksUrl = shouldAttemptPrefetch
    ? null
    : apiUrl && isCustomBlockRoute
      ? `${apiUrl}/custom_blocks`
      : null;
  const {
    data: customBlocksData,
    isLoading: isCustomBlocksLoading,
    swrKey: swrKeyCustomBlocks,
  } = useReadmeApi<ReadCustomBlockGitCollectionType>(customBlocksUrl, {
    ...defaultApiOptions,
    swr: {
      revalidateOnFocus,
      shouldRetryOnError: true,
      ...(suspense ? { suspense: true } : {}),
    },
  });

  // When editing a reference endpoint page, get the URL to fetch the full API
  // definition from the existing document. If none exists (typically for new
  // endpoint pages), fallback to the first API definition of the project.
  const apiDefinitionUrl = useMemo(() => {
    if (section !== 'reference' || !isEditing) return null;
    const data = documentData?.data;
    if (!isReferencePage(data)) return null;
    if (data.type !== 'endpoint') return null;

    if (data.api.uri) {
      // Extract the filename from the URI (supports both /apis/ and /api_definitions/ forms)
      // and build the request URL using getApiBaseUrl() so the branch name is encoded.
      // This fixes 404s on slash-named branches where raw api.uri path segments are unencoded.
      const filename = extractApiDefinitionFilename(data.api.uri);
      if (!filename) return null;
      return `${getApiBaseUrl()}/apis/${filename}`;
    }

    // Default to first API definition when creating a new page.
    if (isCreateNewPage && firstApiDefinition) {
      const filename = firstApiDefinition.filename ?? extractApiDefinitionFilename(firstApiDefinition.uri);
      if (filename) return `${getApiBaseUrl()}/apis/${filename}`;
    }
    return null;
  }, [documentData?.data, firstApiDefinition, getApiBaseUrl, isCreateNewPage, isEditing, section]);

  const {
    data: apiDefinitionData,
    isLoading: isApiDefinitionLoading,
    swrKey: swrKeyApiDefinition,
  } = useReadmeApiNext<ReadAPIDefinitionType>(apiDefinitionUrl, {
    ...defaultApiOptions,
    swr: {
      revalidateOnFocus,
      shouldRetryOnError: true,
      ...(suspense ? { suspense: true } : {}),
    },
  });

  /**
   * Indicates whether data is loading or not. Because we require multiple API
   * fetches to hydrate document data, we must evalute multiple loading states.
   */
  const effectiveDocumentData = useMemo(() => {
    return shouldAttemptPrefetch ? (prefetchData?.file ? { data: prefetchData.file } : undefined) : documentData;
  }, [documentData, prefetchData?.file, shouldAttemptPrefetch]);

  const effectiveCustomBlocksData = useMemo(():
    | ReadCustomBlockGitCollectionType
    | ReadCustomBlockGitUnpaginatedCollectionType
    | undefined => {
    if (shouldAttemptPrefetch && prefetchData && 'custom_blocks' in prefetchData) {
      return prefetchData.custom_blocks;
    }
    return shouldAttemptPrefetch ? undefined : customBlocksData;
  }, [customBlocksData, prefetchData, shouldAttemptPrefetch]);
  const effectiveDocumentLoading = shouldAttemptPrefetch ? isPrefetchLoading : isDocumentLoading;
  const effectiveCustomBlocksLoading = shouldAttemptPrefetch ? isPrefetchLoading : isCustomBlocksLoading;
  const effectiveDocumentKey = shouldAttemptPrefetch ? swrKeyPrefetch : swrKeyDocument;
  const effectiveCustomBlocksKey = shouldAttemptPrefetch ? swrKeyPrefetch : swrKeyCustomBlocks;

  const isLoading = effectiveDocumentLoading || effectiveCustomBlocksLoading || isApiDefinitionLoading;

  /**
   * Indicates whether all initiated hydrations are synced up (e.g. both
   * document and custom blocks hydrations). This is important b/c we only want
   * to update our store after all request states are either pending or
   * completed. This is equivalent to a
   *
   * ```
   * Promise.all(documentFetch, customBlocksFetch, legacyDataFetch)
   * ```
   */
  const isLoadingStateStable = useMemo(() => {
    const loadingStates: boolean[] = [];

    // for every request that was potentially made, check whether the request
    // URL is non-null meaning we actually did initiate it. if it's non-null, we
    // want to include that request's loading state in our list of loading
    // states to compare.
    if (shouldAttemptPrefetch) {
      if (prefetchUrl) {
        loadingStates.push(isPrefetchLoading);
      }
    } else {
      if (documentUrl) {
        loadingStates.push(isDocumentLoading);
      }
      if (customBlocksUrl) {
        loadingStates.push(isCustomBlocksLoading);
      }
    }
    if (apiDefinitionUrl) {
      loadingStates.push(isApiDefinitionLoading);
    }

    // do all the loading states have the same value?
    return loadingStates.every(loadingState => loadingState === loadingStates[0]);
  }, [
    apiDefinitionUrl,
    customBlocksUrl,
    documentUrl,
    isApiDefinitionLoading,
    isCustomBlocksLoading,
    isDocumentLoading,
    isPrefetchLoading,
    prefetchUrl,
    shouldAttemptPrefetch,
  ]);

  /** Page document that's been augmented with full API definition schema. */
  const nextDocumentData = useMemo(() => {
    return produce(effectiveDocumentData, draft => {
      if (draft && apiDefinitionData && isReferencePage(draft.data)) {
        /*
          Deep merge the current document's partial API definition schema
          into the full API definition. Merges happen from left to right, and
          the current document must always take precedence.
        */
        draft.data.api.schema = lodashMerge({}, apiDefinitionData.data.schema, draft.data.api.schema);

        /*
          Need to overwrite tags with the full list since lodashMerge can overwrite specific items.
          Tags are stored in an array [{ name: 'tag1' }, { name: 'tag2' }].
          Since the incoming data is just the tags for a single endpoint, lodashMerge will overwrite
          the first item thinking its been upated. See RM-12731
        */
        if (apiDefinitionData.data.schema.tags) {
          draft.data.api.schema.tags = apiDefinitionData.data.schema.tags;
        }
        draft.data.api.uri = apiDefinitionData.data.uri;
      }
    });
  }, [apiDefinitionData, effectiveDocumentData]);

  const initializePayload = useMemo(
    () => ({
      customBlocks: effectiveCustomBlocksData?.data || [],
      data: nextDocumentData?.data || null,
      etag: documentEtag,
      isLoading,
      swrKeyCustomBlocks: effectiveCustomBlocksKey,
      swrKeyDocument: effectiveDocumentKey,
      swrKeyApiDefinition,
    }),
    [
      documentEtag,
      effectiveCustomBlocksData?.data,
      effectiveCustomBlocksKey,
      effectiveDocumentKey,
      isLoading,
      nextDocumentData?.data,
      swrKeyApiDefinition,
    ],
  );

  // Suspense mode: useLayoutEffect syncs store before paint to prevent stale content flash.
  useIsomorphicLayoutEffect(() => {
    if (!suspense) return;
    if (isLoadingStateStable) {
      initialize(initializePayload);
    }
  }, [initialize, initializePayload, isLoadingStateStable, suspense]);

  useEffect(() => {
    if (suspense) return;
    if (isLoadingStateStable) {
      initialize(initializePayload);
    }
  }, [initialize, initializePayload, isLoadingStateStable, suspense]);

  return <>{children}</>;
}
